import { Component, Injector, Input, OnInit } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { tap, filter } from 'rxjs/operators';
import moment from 'moment-timezone';

import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { SavedFilterService } from '@Common/Filter/services/saved-filter.service';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { UsersService } from '@Core/Users/services/users.service';
import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { SavedFilterSetupDialogComponent } from '@Monitor/Shared/components/SavedFilterSetupDialog/saved-filter-setup-dialog.component';
import { SavedFilter } from '@Common/Filter/models/saved-filter.model';
import { SavedFilterSetupMode } from '@Monitor/Shared/enums/saved-filter-setup-mode.enum';

class SavedFilterCard extends SavedFilter {
    selected: boolean;
    created: string;
    modified: string;
    createdBy: string;
    modifiedBy: string;
}

@Component({
    selector: 'agpr-saved-filter-manage-dialog',
    templateUrl: './saved-filter-manage-dialog.component.html',
    styleUrls: ['./saved-filter-manage-dialog.component.scss'],
    standalone: false
})
export class SavedFilterManageDialogComponent extends AbstractDialogComponent implements OnInit {
    @Input() id: string = 'savedFilterManageDialog';

    searchText: string = '';
    dateFormat: string;
    allFiltersSelected: boolean = false;
    sortOrder: SortOrderEnum = SortOrderEnum.ascending;

    listItems: SavedFilterCard[] = [];
    selectedItems: SavedFilterCard[] = [];
    filteredItems: SavedFilterCard[] = [];
    hasFavoriteItems: boolean = false;
    loading: boolean = false;

    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly sortOrderEnum = SortOrderEnum;

    constructor(
        coreDialogService: CoreDialogService,
        private savedFilterService: SavedFilterService,
        protected injector: Injector,
        public currentUserStateService: CurrentUserStateService,
        private usersService: UsersService
    ) {
        super(coreDialogService, 'SavedFilterManageDialog');

        this.subscriptions.push(
            this.savedFilterService.all$.subscribe(
                listItems => {
                    if (listItems) {
                        this.listItems = listItems as SavedFilterCard[];
                        this.setData();
                        this.filteredItems = this.listItems.slice();
                        this.hasFavoriteItems = !!this.filteredItems.find(x => x.favorite);
                        this.sortItems();
                        this.setFilteredListItems();
                    }
                }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.dateFormat = prefs.getDateFormatString(true);
                this.setData();
            })
        );
    }

    ngOnInit() {}

    setData() {
        if (!this.dateFormat) {
            return;
        }
        const userIds = [];
        this.listItems.forEach(item => {
            userIds.push(item.createdByUserId);
            item.created = moment(item.createdDate).format(this.dateFormat);
            item.selected = this.selectedItems.includes(item);

            if (item.modifiedByUserId) {
                userIds.push(item.modifiedByUserId);
                if (moment(item.modifiedDate).isValid()) {
                    item.modified = moment(item.modifiedDate).format(this.dateFormat);
                }
            }
        });

        this.getUsers(userIds);
    }

    getUsers(userIds: number[]) {
        this.usersService.getUsers(userIds).subscribe(users => {
            const userMap = {};
            users.forEach((user) => {
                userMap[user.id] = user.getDisplayName();
            });
            this.listItems.forEach(item => {
                item.createdBy = userMap[item.createdByUserId];
                if (item.modifiedByUserId) {
                    item.modifiedBy = userMap[item.modifiedByUserId];
                }
            });
        });
    }


    setFilteredListItems(): void {
        this.filteredItems = this.listItems?.filter(
            (listItem) => {
                return listItem.name.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1;
            }
        );
        this.hasFavoriteItems = !!this.filteredItems.find(x => x.favorite);
    }

    toggleSelectAll() {
        this.allFiltersSelected = !this.allFiltersSelected;
        this.filteredItems.forEach(item => {
            item.selected = this.allFiltersSelected;
        });
        this.checkForSelected();
    }

    checkForSelected() {
        this.selectedItems = this.filteredItems.filter(item => !!item && item.selected);
        this.allFiltersSelected = this.selectedItems.length === this.filteredItems.length;
    }

    toggleSortOrder() {
        this.sortOrder = this.sortOrder === SortOrderEnum.ascending ? SortOrderEnum.descending : SortOrderEnum.ascending;
        this.sortItems();
    }

    sortItems() {
        this.filteredItems.sort((a, b) => {
            return a.name.trim().localeCompare(b.name.trim());
        });
        if (this.sortOrder !== SortOrderEnum.ascending) {
            this.filteredItems.reverse();
        }
        this.filteredItems = this.filteredItems.slice();
    }

    openDeleteConfirmationDialog(selectedItems: SavedFilterCard[]) {
        const io: ConfirmDialogModel = {
            inputs: {
                id: 'savedFilterManageDialog.deleteConfirmationDialog',
                heading: 'Common.filter.savedFilters.deleteConfirmationDialog.heading',
                alertType: messageSeverityEnum.warning,
                okLabel: 'delete',
                alertMessage: 'Common.filter.savedFilters.deleteConfirmationDialog.warningMessage',
                list: selectedItems.map(x => x.name),
                confirmAction: () => {
                    if (selectedItems.length === 1) {
                        return this.savedFilterService.delete(selectedItems[0]).pipe(
                            tap(() => this.selectedItems = [])
                        );
                    } else {
                        return this.savedFilterService.bulkDelete(selectedItems.map(x => x.id)).pipe(
                            tap(() => this.selectedItems = [])
                        );
                    }
                }
            },
            outputs: {
                dialogClose: () => {}
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    editFavorite(item: SavedFilterCard) {
        this.loading = true;

        this.savedFilterService.updateFavorite(item.id, !item.favorite).subscribe({
            next: () => this.loading = false,
            error: () => this.loading = false
        });
    }


    openSavedFilterSetupDialog(selectedItem?: SavedFilterCard) {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const overlayContainer = document.getElementById('main-content');

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer,
                mode: selectedItem ? SavedFilterSetupMode.edit : SavedFilterSetupMode.create,
                savedFilterSummary: selectedItem ? selectedItem : null
            }
        };
        this.coreDialogService.open(SavedFilterSetupDialogComponent, io, this.injector);
    }

}
